/**
 * SCQ funnel — results page (<scq-results>).
 *
 * Reads the quiz state (storage -> URL -> default), renders the skin-type box / personalized headline /
 * doctor caption from the section's JSON config, mounts the concern-bucketed Vidalytics VSL and runs the
 * watch gate (window.ScqVidalytics). The routine box is hidden until unlock; unlocking IS the reveal.
 * Port of kk-headless scq/quiz/results/page.tsx.
 */
if (!customElements.get('scq-results')) {
  customElements.define(
    'scq-results',
    class ScqResults extends HTMLElement {
      constructor() {
        super();
        this.unlocked = false;
        this.unlockTracked = false;
        this.detach = null;
        this.onCtaClick = this.onCtaClick.bind(this);
      }

      connectedCallback() {
        var S = window.ScqStorage;
        var V = window.ScqVidalytics;
        if (!S) {
          console.warn('[scq-results] ScqStorage missing');
          return;
        }
        this.config = this.readConfig();
        if (!this.config) return;

        S.track('page_view', { page: 'scq-results' });
        if (V) V.warm();

        this.state = this.resolveState();
        this.concern = this.state.answers['3'] || 'wrinkles';
        this.typeInfo = this.lookupType(this.state.primary);
        this.secondaryInfo = this.state.secondary ? this.lookupType(this.state.secondary) : null;
        this.concernInfo = this.lookupConcern(this.concern);
        this.embedId = this.resolveEmbedId();

        this.renderTypeBox();
        this.renderHeadline();
        this.mountVideo();

        var cta = this.querySelector('[data-scq-cta]');
        if (cta) cta.addEventListener('click', this.onCtaClick);

        var params = new URLSearchParams(window.location.search);
        var persisted = S.safeGet(S.KEYS.routineUnlocked, 'local') === 'true';
        var alreadyUnlocked = params.get('skip') === '1' || persisted || this.config.unlockPercent === 0;
        if (alreadyUnlocked) {
          // Not a genuine reveal — show the box but do not fire routine_unlocked.
          this.unlockTracked = true;
          this.reveal();
        }
        this.attachGate(alreadyUnlocked);
      }

      disconnectedCallback() {
        if (this.detach) this.detach();
        var cta = this.querySelector('[data-scq-cta]');
        if (cta) cta.removeEventListener('click', this.onCtaClick);
      }

      readConfig() {
        var node = this.querySelector('[data-scq-results-config]');
        try {
          return JSON.parse(node.textContent);
        } catch (e) {
          console.warn('[scq-results] invalid config JSON', e);
          return null;
        }
      }

      /** Storage -> URL -> default (collagen-depletion + wrinkles); default persisted only when nothing stored. */
      resolveState() {
        var S = window.ScqStorage;
        var state = S.readQuizState();
        if (!state.primary) {
          var answers = Object.keys(state.answers || {}).length ? state.answers : { 3: 'wrinkles' };
          state = { primary: 'collagen-depletion', secondary: null, answers: answers };
          if (!S.safeGet(S.KEYS.primaryType, 'local')) {
            S.safeSet(S.KEYS.primaryType, state.primary, 'local');
            if (!S.safeGet(S.KEYS.answers, 'local')) S.safeSet(S.KEYS.answers, JSON.stringify(answers), 'local');
          }
        }
        return state;
      }

      lookupType(key) {
        var types = this.config.skinTypes || [];
        for (var i = 0; i < types.length; i++) if (types[i].key === key) return types[i];
        return types[0] || null;
      }

      lookupConcern(key) {
        var list = this.config.concerns || [];
        var fallback = null;
        for (var i = 0; i < list.length; i++) {
          if (list[i].key === key) return list[i];
          if (list[i].key === 'wrinkles') fallback = list[i];
        }
        return fallback || list[0] || null;
      }

      /** Concern -> slot (v1/v2/v3) -> embed id; ?vid=<shortId> overrides for QA. */
      resolveEmbedId() {
        var vid = new URLSearchParams(window.location.search).get('vid');
        if (vid && /^[A-Za-z0-9_-]+$/.test(vid)) return 'vidalytics_embed_' + vid;
        var slot = (this.concernInfo && this.concernInfo.videoSlot) || 'v1';
        return this.config.embeds[slot] || this.config.embeds.v1;
      }

      setText(selector, text) {
        var el = this.querySelector(selector);
        if (el) el.textContent = text == null ? '' : text;
      }

      renderTypeBox() {
        var box = this.querySelector('[data-scq-type-box]');
        if (!box || !this.typeInfo) return;
        var t = this.typeInfo;
        this.setText('[data-scq-type-name]', t.name);
        var secondary = this.querySelector('[data-scq-secondary]');
        if (secondary) {
          if (this.secondaryInfo && this.secondaryInfo.key !== t.key) {
            this.setText('[data-scq-secondary-name]', this.secondaryInfo.shortName || this.secondaryInfo.name);
            secondary.hidden = false;
          } else {
            secondary.hidden = true;
          }
        }
        this.setText('[data-scq-severity-label]', t.severityLabel);
        this.setText('[data-scq-severity-level]', t.severityLevel);
        this.setText('[data-scq-tagline]', t.tagline);
        var pct = Math.max(0, Math.min(100, Number(t.severityPct) || 0));
        var meter = this.querySelector('[data-scq-meter]');
        if (meter) meter.setAttribute('aria-valuenow', String(pct));
        var thumb = this.querySelector('[data-scq-thumb]');
        if (thumb) {
          // Animate from 0 -> pct (transition: left 1.5s).
          thumb.style.left = '0%';
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              thumb.style.left = pct + '%';
            });
          });
        }
      }

      renderHeadline() {
        var c = this.concernInfo || {};
        this.setText('[data-scq-profile]', c.profile);
        this.setText('[data-scq-headline]', c.headline);
        this.setText('[data-scq-cause]', c.cause);
      }

      mountVideo() {
        var container = this.querySelector('[data-scq-video]');
        if (!container || !this.embedId) return;
        var embed = document.createElement('div');
        embed.id = this.embedId;
        embed.style.width = '100%';
        embed.style.position = 'relative';
        embed.style.paddingTop = '56.25%';
        container.appendChild(embed);
        if (window.ScqVidalytics) window.ScqVidalytics.load(this.config.account, this.embedId);
      }

      attachGate(alreadyUnlocked) {
        var V = window.ScqVidalytics;
        var S = window.ScqStorage;
        var self = this;
        var cfg = this.config;
        if (!V) {
          // Loader missing entirely: never trap the viewer.
          this.unlock('fail-open', 'no-loader', false);
          return;
        }
        var duration = Number(cfg.fallbackDurationSeconds) || 2129;
        var unlockPercent = Number(cfg.unlockPercent);
        if (isNaN(unlockPercent)) unlockPercent = 50;
        var watchTimeSeconds = unlockPercent > 0 ? Math.round((duration * unlockPercent) / 100) : 0;

        this.detach = V.attachProgress(this.embedId, {
          initiallyUnlocked: alreadyUnlocked,
          fallbackDurationSeconds: duration,
          unlockPercent: unlockPercent > 0 ? unlockPercent : 101,
          watchTimeUnlockSeconds: watchTimeSeconds,
          milestoneKey: 'scq_results_milestones_' + this.embedId,
          onMilestone: function (key, extra) {
            S.track('video_milestone', Object.assign({ milestone: key, skinType: self.state.primary }, extra));
          },
          onProgress: function (pct) {
            if (unlockPercent > 0 && pct >= unlockPercent) self.unlock('percent', null, true);
          },
          onEnded: function () {
            self.unlock('ended', null, true);
          },
          onCtaShow: function () {
            self.unlock('cta-show', null, true);
          },
          onWatchTimeReached: function () {
            console.warn('[scq-results] routine unlocked via wall-clock watch-time net — percent gate missed');
            self.unlock('watch-time', null, true);
          },
          onUnavailable: function (reason) {
            console.warn('[scq-results] Vidalytics detection failed (' + reason + ') — failed open to reveal routine');
            self.unlock('fail-open', reason, false);
          },
        });
      }

      /** First path wins. `persist` writes scq_routine_unlocked; fail-open never persists. */
      unlock(source, reason, persist) {
        var S = window.ScqStorage;
        if (persist) S.safeSet(S.KEYS.routineUnlocked, 'true', 'local');
        this.reveal();
        if (this.unlockTracked) return;
        this.unlockTracked = true;
        var props = { source: source, page: 'results', embed_id: this.embedId };
        if (reason) props.reason = reason;
        S.track('routine_unlocked', props);
      }

      reveal() {
        if (this.unlocked) return;
        this.unlocked = true;
        var box = this.querySelector('[data-scq-routine]');
        if (!box) return;
        box.hidden = false;
        box.classList.add('scq-fade-in-up');
        var cta = this.querySelector('[data-scq-cta]');
        if (cta) cta.removeAttribute('aria-disabled');
      }

      /** Hard navigation (router-style pushes fail after a suspended mobile tab). */
      onCtaClick(event) {
        event.preventDefault();
        if (!this.unlocked) return;
        var S = window.ScqStorage;
        var url = this.config.orderUrl;
        if (!url) {
          console.warn('[scq-results] order_url is not set on the section');
          return;
        }
        if (!S.storageAvailable() && this.state.primary) url = S.appendQuizStateToUrl(url, this.state);
        window.location.href = url;
      }
    }
  );
}
